import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';
import type { BrokerAccount } from '../../types';
import { getBrandClasses } from '../../config/tenantConfig';

interface BrokerHealthCardsProps {
  accounts: BrokerAccount[];
  loading?: boolean;
}

function tokenStatus(account: BrokerAccount): 'valid' | 'expiring' | 'expired' {
  if (account.requires_reauth || account.is_oauth_valid === false) return 'expired';
  if (!account.has_oauth_token) return 'expired';
  if (account.token_expires_at) {
    const expiresAt = new Date(account.token_expires_at).getTime();
    const now = Date.now();
    if (expiresAt < now) return 'expired';
    if (expiresAt - now < 60 * 60 * 1000) return 'expiring';
  }
  return 'valid';
}

const dotColor: Record<string, string> = {
  valid: 'bg-green-500',
  expiring: 'bg-amber-500',
  expired: 'bg-red-500',
};

const BrokerHealthCards: React.FC<BrokerHealthCardsProps> = ({ accounts, loading }) => {
  const brand = getBrandClasses();

  if (loading) {
    return (
      <div className="flex items-center gap-3 px-1">
        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 px-1">
        <span>No brokers connected</span>
        <span className="text-gray-300 dark:text-gray-600">&middot;</span>
        <Link to="/brokers" className={`font-medium ${brand.link} hover:underline`}>
          Connect a broker
        </Link>
      </div>
    );
  }

  const expiredAccounts = accounts.filter(a => tokenStatus(a) === 'expired');

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 px-1">
      {accounts.map(account => {
        const status = tokenStatus(account);
        return (
          <span key={account.client_id} className="flex items-center gap-1.5">
            <span className={cn('w-1.5 h-1.5 rounded-full', dotColor[status])} />
            <span className="text-gray-700 dark:text-gray-300 font-medium">{account.client_id}</span>
            <span className="text-gray-400 dark:text-gray-500 capitalize">{account.broker_name}</span>
          </span>
        );
      })}
      {expiredAccounts.length > 0 && (
        <>
          <span className="text-gray-300 dark:text-gray-600">&middot;</span>
          <Link to="/brokers" className="font-medium text-red-500 dark:text-red-400 hover:underline">
            {expiredAccounts.length} {expiredAccounts.length === 1 ? 'broker needs' : 'brokers need'} re-authentication
          </Link>
        </>
      )}
    </div>
  );
};

export default BrokerHealthCards;
